
import React, { useEffect, useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Classes from "./Membership.module.css";
import { useNavigate } from "react-router-dom";
import NotifyPopup from "../../../../Panels/GlobalComponents/NotifyPopup/NotifyPopup";
import { EmailSentIcon, ExclamationIcon } from "../../../../utils/Icons";
import { useSelector } from "react-redux";
import backend from "../../../../api";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
const cardIn = {
  hidden: { opacity: 0, y: 14, scale: 0.985 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35 } },
};

const labelFor = (key) => {
  if (!key || key === "free") return "None";
  if (key === "tier1") return "Tier 1";
  if (key === "tier2") return "Tier 2";
  if (key === "tier3") return "Tier 3";
  if (key === "fleet") return "Fleet";
  return key?.charAt(0).toUpperCase() + key?.slice(1);
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

function isSubActive(activeSub) {
  if (!activeSub) return false;
  const status = (activeSub.status || "none").toLowerCase();
  if (status === "none" || status === "canceled" || status === "cancelled") return false;

  const now = Date.now();
  const startOk = activeSub.current_period_start ? new Date(activeSub.current_period_start).getTime() <= now : true;
  const endOk = activeSub.current_period_end ? new Date(activeSub.current_period_end).getTime() >= now : false;
  return startOk && endOk;
}

const n = (v, fallback = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
};

const vehicleText = (limit) => {
  const lim = Number(limit);
  if (!Number.isFinite(lim)) return null;
  if (lim === 0) return "No Vehicle Limit";
  return lim === 1 ? "1 Vehicle" : `${lim} Vehicles`;
};

export default function OnboardingMembership() {
  const navigateTo = useNavigate();
  const { user, token } = useSelector((s) => s?.auth || {});
  const userId = user?._id;
  const companyId = user?.companyId;

  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  const [billing, setBilling] = useState("monthly"); // monthly | biannually | annually
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);
  const isMobile = width < 768;

  const [snapshot, setSnapshot] = useState(null);
  const [loadingSnap, setLoadingSnap] = useState(false);

  // ✅ DB plans
  const [dbPlans, setDbPlans] = useState(null); // null = loading
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Popups
  const [openNotifyPopup, setopenNotifyPopup] = useState(false);
  const [openTouchSoonPopup, setopenTouchSoonPopup] = useState(false);

  const handleOpenNotifyPopup = () => setopenNotifyPopup(true);
  const handleCloseNotifyPopup = () => setopenNotifyPopup(false);
  const handleSubmitNotify = () => navigateTo("/");

  const handleOpenTouchSoonPopup = () => setopenTouchSoonPopup(true);
  const handleCloseTouchSoonPopup = () => setopenTouchSoonPopup(false);
  const handleSubmitTouchSoon = () => navigateTo("/");

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Load subscription snapshot
  useEffect(() => {
    async function loadMe() {
      if (!userId) return;
      setLoadingSnap(true);
      try {
        const { data } = await backend.get(`/api/stripe/subscriptions/me/${userId}`, { headers });
        setSnapshot(data || {});
      } catch (e) {
        console.error("load sub snapshot error:", e);
        setSnapshot(null);
      } finally {
        setLoadingSnap(false);
      }
    }
    loadMe();
  }, [userId, token]);

  // ✅ Load plans from DB (PlanConfig)
  useEffect(() => {
    async function loadPlans() {
      setLoadingPlans(true);
      try {
        const { data } = await backend.get("/api/plan-config", { headers });
        const items = Array.isArray(data?.items) ? data.items : [];
        const order = ["tier1", "tier2", "tier3", "fleet"];
        items.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
        setDbPlans(items);
      } catch (e) {
        console.error("load plans error:", e);
        setDbPlans([]);
      } finally {
        setLoadingPlans(false);
      }
    }
    loadPlans();
  }, [token]);

  const active = snapshot?.activeSubscription || {};
  const currentPlanKey = active?.plan?.key || snapshot?.legacyLabel || "free";
  const status = active?.status || "none";
  const cancelAtEnd = !!active?.cancel_at_period_end;
  const periodStart = active?.current_period_start;
  const periodEnd = active?.current_period_end;
  const subIsActive = isSubActive(active);

  const cycleMap = { monthly: "monthly", biannually: "biannually", annually: "annually" };

  // ✅ Build UI plans (same labels/text, amounts from DB)
  const plans = useMemo(() => {
    if (dbPlans === null || loadingPlans) {
      return [
        { key: "sk_tier1", __skeleton: true, highlight: false },
        { key: "sk_tier2", __skeleton: true, highlight: true },
        { key: "sk_tier3", __skeleton: true, highlight: false },
      ];
    }

    const byKey = new Map((dbPlans || []).map((p) => [p.key, p]));
    const t1 = byKey.get("tier1");
    const t2 = byKey.get("tier2");
    const t3 = byKey.get("tier3");

    const t1m = n(t1?.pricing?.monthly?.amount, 0);
    const t1b = n(t1?.pricing?.biannually?.amount, 0);
    const t1a = n(t1?.pricing?.annually?.amount, 0);

    const t2m = n(t2?.pricing?.monthly?.amount, 20);
    const t2b = n(t2?.pricing?.biannually?.amount, 108);
    const t2a = n(t2?.pricing?.annually?.amount, 220);

    const t3m = n(t3?.pricing?.monthly?.amount, 40);
    const t3b = n(t3?.pricing?.biannually?.amount, 216);
    const t3a = n(t3?.pricing?.annually?.amount, 440);

    const t1Vehicle = vehicleText(t1?.vehiclesLimit) || "No Vehicle Limit";
    const t2Vehicle = vehicleText(t2?.vehiclesLimit) || "1 Vehicle";
    const t3Vehicle = vehicleText(t3?.vehiclesLimit) || "2 Vehicles";

    return [
      {
        key: "tier1",
        title: "Tier 1",
        subtitle: "Unlimited fill-ups, $9.99 fill-up fee",
        highlight: false,
        pricing: {
          monthly: { amount: `${t1m}`, unit: "/forever", billedNote: "Free Forever", cta: "Get started for free" },
          biannually: { amount: `${t1b}`, unit: "/forever", billedNote: "Free Forever", cta: "Get started for free" },
          annually: { amount: `${t1a}`, unit: "/forever", billedNote: "Free Forever", cta: "Get started for free" },
        },
        features: [t1Vehicle, "Unlimited Fill-Ups", "Fuel at Cost", "No Set Up Fee"],
      },
      {
        key: "tier2",
        title: "Tier 2",
        subtitle: "1 vehicle and 4 fill-ups per month",
        highlight: true,
        pricing: {
          monthly: { amount: `${t2m}`, unit: "/month", billedNote: "Billed Every 6 Months", cta: "Start started - 1 month free" },
          biannually: { amount: `${t2b}`, unit: "/6 months", billedNote: "Billed Bi-Annually", cta: "Get started for 10% off" },
          annually: { amount: `${t2a}`, unit: "/year", billedNote: "Billed Annually", cta: "Get started - 1 month free" },
        },
        features: [t2Vehicle, "4 Fill-Ups Per Month", "Fuel at Cost", "No Set Up Fee"],
      },
      {
        key: "tier3",
        title: "Tier 3",
        subtitle: "2 vehicles, 4 fill-ups per month per vehicle",
        highlight: false,
        pricing: {
          monthly: { amount: `${t3m}`, unit: "/month", billedNote: "Billed Monthly", cta: "Start started - 2 months free" },
          biannually: { amount: `${t3b}`, unit: "/6 months", billedNote: "Billed Bi-Annually", cta: "Get started for 10% off" },
          annually: { amount: `${t3a}`, unit: "/year", billedNote: "Billed Annually", cta: "Get started - 1 month free" },
        },
        features: [t3Vehicle, "4 Fill-Ups Per Month Per Vehicle", "Fuel at Cost", "No Set Up Fee"],
      },
    ];
  }, [dbPlans, loadingPlans]);

  const getCurrentPricing = (plan) => plan.pricing[billing];

  const priceLabel = (plan) => {
    const p = getCurrentPricing(plan);
    return (
      <>
        <span className={Classes.price}>${p.amount}</span>
        <span className={Classes.per}>{p.unit}</span>
      </>
    );
  };

  // ✅ Overview table now uses DB vehicle limits (text stays same style)
  const overviewRows = useMemo(() => {
    const byKey = new Map((dbPlans || []).map((p) => [p.key, p]));
    const t1 = byKey.get("tier1");
    const t2 = byKey.get("tier2");
    const t3 = byKey.get("tier3");

    const t1Veh = vehicleText(t1?.vehiclesLimit) || "No Vehicle Limit";
    const t2Veh = String(n(t2?.vehiclesLimit, 1));
    const t3Veh = String(n(t3?.vehiclesLimit, 2));

    return [
      { label: "Vehicles", tier1: t1Veh, tier2: t2Veh, tier3: t3Veh },
      { label: "Fill Ups Per Month", tier1: "Unlimited", tier2: "4", tier3: "4 Per Vehicle" },
      { label: "Fuel at Cost", tier1: "Yes", tier2: "Yes", tier3: "Yes" },
      { label: "Set Up Fee (One Time)", tier1: "$0", tier2: "$0", tier3: "$0" },
    ];
  }, [dbPlans]);

  // Checkout
  async function startSubscription(planKey, vehiclesCount = 0) {
    if (!userId) return;
    try {
      const billingCycle = cycleMap[billing] || "monthly";
      const payload = {
        userId,
        companyId,
        planKey,
        billingCycle,
        vehiclesCount,
        success_url: `${window.location.origin}/settings/membership?success=1`,
        cancel_url: `${window.location.origin}/settings/membership?canceled=1`,
      };

      const { data } = await backend.post("/api/stripe/subscriptions/checkout", payload, { headers });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      console.error("checkout error:", err);
    }
  }

  const handleGetStarted = (planKey) => {
    if (isMobile) {
      window.open("https://apps.apple.com/us/app/fuelclubs/id6753362541", "_blank");
    } else {
      startSubscription(planKey);
    }
  };

  const isPlanSubscribed = (pKey) => subIsActive && pKey === currentPlanKey;

  const toggleBgLeft =
    billing === "monthly"
      ? "4px"
      : billing === "biannually"
      ? "calc(33.333% + 4px)"
      : "calc(66.666% + 4px)";

  return (
    <>
      <div className={Classes.header}>
        <a href="/">
          <img src="/assets/images/logo.webp" draggable={false} alt="Logo" />
        </a>
      </div>

      <motion.section className={Classes.page}  whileInView="show" viewport={{ once: true, amount: 0.2 }}>
        <motion.h1 className={Classes.title} variants={fadeUp}>
          Choose Your Membership
        </motion.h1>

        {/* current membership box kept (still commented as you had it) */}
        <motion.div className={Classes.currentBox}  animate="show" variants={fadeUp}>
          {/* Uncomment if you want to show active membership during onboarding
          <div className={Classes.currentRow}>
            <div>
              <div className={Classes.currentLabel}>Current Membership</div>
              <div className={Classes.currentVal}>
                {loadingSnap ? "Loading..." : labelFor(currentPlanKey)}
                {status && status !== "none" ? <span className={Classes.badge}>{status}</span> : null}
                {cancelAtEnd ? <span className={Classes.badgeWarn}>cancels at period end</span> : null}
              </div>
            </div>
            <div className={Classes.periodWrap}>
              <div className={Classes.period}>
                <span>Period Start</span>
                <strong>{fmt(periodStart)}</strong>
              </div>
              <div className={Classes.period}>
                <span>Period End</span>
                <strong>{fmt(periodEnd)}</strong>
              </div>
            </div>
          </div>
          */}
        </motion.div>

        {/* Billing toggle */}
        <motion.div className={Classes.toggleWrap} variants={fadeUp}>
          <div className={Classes.toggle}>
            <motion.span className={Classes.toggleBg} animate={{ left: toggleBgLeft }} transition={{ type: "spring", stiffness: 520, damping: 32 }} />
            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === "monthly" ? Classes.active : ""}`}
              onClick={() => setBilling("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === "biannually" ? Classes.active : ""}`}
              onClick={() => setBilling("biannually")}
            >
              Bi-Annually
            </button>
            <button
              type="button"
              className={`${Classes.toggleBtn} ${billing === "annually" ? Classes.active : ""}`}
              onClick={() => setBilling("annually")}
            >
              Annually
            </button>
          </div>

          <div className={Classes.toggleNote}>Get 1 month free by billing annually</div>

          <motion.button className={Classes.footerLink} onClick={handleOpenNotifyPopup} variants={fadeUp}>
            Proceed without a membership
          </motion.button>
        </motion.div>

        {/* Plans */}
        <motion.div className={Classes.grid} variants={stagger}>
          <div className={Classes.extraCard} />

          {plans.map((p) => {
            if (p.__skeleton) {
              return (
                <motion.div
                  key={p.key}
                  className={`${Classes.card} ${p.highlight ? Classes.cardHighlight : ""}`}
                  variants={cardIn}
                  whileHover={{ y: -6 }}
                  style={{ opacity: 0.55, pointerEvents: "none" }}
                >
                  <div className={Classes.cardHead}>
                    <div className={Classes.cardTitle}>Tier</div>
                    <div className={Classes.cardSubtitle}>Loading...</div>
                  </div>
                  <div className={Classes.priceRow}>
                    <span className={Classes.price}>$—</span>
                    <span className={Classes.per}></span>
                  </div>
                  <div className={Classes.billed}>Loading...</div>
                  <button type="button" disabled className={`${Classes.cta} ${Classes.disabledBtn}`}>
                    Loading...
                  </button>
                </motion.div>
              );
            }

            const subscribed = isPlanSubscribed(p.key);
            const currentPricing = getCurrentPricing(p);

            return (
              <motion.div
                key={p.key}
                className={`${Classes.card} ${p.highlight ? Classes.cardHighlight : ""}`}
                variants={cardIn}
                whileHover={{ y: -6 }}
              >
                <div className={Classes.cardHead}>
                  <div className={Classes.cardTitle}>{p.title}</div>
                  <div className={Classes.cardSubtitle}>{p.subtitle}</div>
                </div>

                <div className={Classes.priceRow}>{priceLabel(p)}</div>
                <div className={Classes.billed}>{currentPricing.billedNote}</div>

                <button
                  type="button"
                  disabled={subscribed}
                  aria-disabled={subscribed}
                  onClick={subscribed ? undefined : () => handleGetStarted(p.key)}
                  className={`${Classes.cta} ${subscribed ? Classes.disabledBtn : ""}`}
                >
                  {subscribed ? "Subscribed" : currentPricing.cta}
                </button>

                {/* If you ever want to show features list in onboarding, you can uncomment:
                <ul className={Classes.featureList}>
                  {p.features.map((f, idx) => <li key={idx}>{f}</li>)}
                </ul>
                */}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Overview Table */}
        <motion.div className={Classes.overview} variants={fadeUp}>
          <div className={Classes.overviewLabel}>Overview</div>

          <div className={Classes.table}>
            <div className={`${Classes.tr} ${Classes.trHead}`}>
              <div className={Classes.td} />
              <div className={Classes.td}>Tier 1</div>
              <div className={Classes.td}>Tier 2</div>
              <div className={Classes.td}>Tier 3</div>
            </div>

            {overviewRows.map((r, i) => (
              <div className={Classes.tr} key={i}>
                <div className={`${Classes.td} ${Classes.rowLabel}`}>{r.label}</div>
                <div className={Classes.td} data-th="Tier 1">{r.tier1}</div>
                <div className={Classes.td} data-th="Tier 2">{r.tier2}</div>
                <div className={Classes.td} data-th="Tier 3">{r.tier3}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.section>

      {/* Popups */}
      <AnimatePresence exit="wait">
        {openNotifyPopup && (
          <NotifyPopup
            closePopup={handleCloseNotifyPopup}
            submitPopup={handleSubmitNotify}
            title="Are you sure you want to proceed without a membership?"
            description={"Without a membership, you are charged a $9.99 fee per fill up."}
            buttonText="Proceed"
            icon={<ExclamationIcon fill={"white"} height="50px" />}
          />
        )}
      </AnimatePresence>

      <AnimatePresence exit="wait">
        {openTouchSoonPopup && (
          <NotifyPopup
            closePopup={handleCloseTouchSoonPopup}
            submitPopup={handleSubmitTouchSoon}
            title="We’ll Be in Touch Soon"
            description={"Your inquiry for membership has been sent successfully. A member of our team will be in touch shortly."}
            buttonText="Return"
            icon={<EmailSentIcon fill={"white"} />}
          />
        )}
      </AnimatePresence>
    </>
  );
}
